import { useMemo, useState } from "react";
import { ChevronDown, ChevronUp, Search, X } from "lucide-react";
import { cn } from "@/lib/utils/cn";

export type SearchableValuePickerOption = {
  value: string;
  label?: string;
  hint?: string;
  group?: string;
};

type SearchableValuePickerProps = {
  values: string[];
  onChange: (values: string[]) => void;
  options: SearchableValuePickerOption[];
  placeholder?: string;
  allowCustom?: boolean;
  multiple?: boolean;
  className?: string;
};

function normalizeValue(value: string): string {
  return value.trim();
}

function formatOptionLabel(option: SearchableValuePickerOption): string {
  return option.label?.trim() || option.value;
}

function matchesQuery(option: SearchableValuePickerOption, query: string): boolean {
  if (!query) {
    return true;
  }
  const haystack = [option.value, option.label, option.hint, option.group]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
  return haystack.includes(query);
}

export function SearchableValuePicker({
  values,
  onChange,
  options,
  placeholder = "Search or type a value",
  allowCustom = true,
  multiple = true,
  className,
}: SearchableValuePickerProps) {
  const [query, setQuery] = useState("");

  const optionByValue = useMemo(() => {
    return new Map(options.map((option) => [normalizeValue(option.value), option] as const));
  }, [options]);

  const selected = useMemo(() => {
    const seen = new Set<string>();
    const next: string[] = [];
    for (const raw of values) {
      const value = normalizeValue(raw);
      if (!value || seen.has(value)) {
        continue;
      }
      seen.add(value);
      next.push(value);
    }
    return next;
  }, [values]);

  const filteredOptions = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    const selectedSet = new Set(selected);
    return options
      .filter((option) => matchesQuery(option, normalizedQuery))
      .filter((option) => (multiple ? !selectedSet.has(normalizeValue(option.value)) : true))
      .toSorted((left, right) => {
        const leftGroup = left.group?.trim() || "";
        const rightGroup = right.group?.trim() || "";
        const groupCompare = leftGroup.localeCompare(rightGroup);
        if (groupCompare !== 0) {
          return groupCompare;
        }
        return formatOptionLabel(left).localeCompare(formatOptionLabel(right));
      })
      .slice(0, 12);
  }, [options, query, selected, multiple]);

  const replaceValues = (next: string[]) => {
    const normalized = next.map(normalizeValue).filter(Boolean);
    onChange(multiple ? normalized : normalized.slice(0, 1));
  };

  const addValue = (raw: string) => {
    const value = normalizeValue(raw);
    if (!value) {
      return;
    }
    if (multiple) {
      if (!selected.includes(value)) {
        replaceValues([...selected, value]);
      }
    } else {
      replaceValues([value]);
    }
    setQuery("");
  };

  const removeValue = (index: number) => {
    replaceValues(selected.filter((_, current) => current !== index));
  };

  const moveValue = (index: number, direction: -1 | 1) => {
    const target = index + direction;
    if (target < 0 || target >= selected.length) {
      return;
    }
    const next = [...selected];
    const [item] = next.splice(index, 1);
    next.splice(target, 0, item);
    replaceValues(next);
  };

  const commitQuery = () => {
    const trimmed = normalizeValue(query);
    if (!trimmed) {
      return;
    }
    const exactMatch = options.find((option) => normalizeValue(option.value) === trimmed);
    if (exactMatch) {
      addValue(exactMatch.value);
      return;
    }
    if (allowCustom) {
      addValue(trimmed);
    }
  };

  const renderChip = (value: string, index: number) => {
    const option = optionByValue.get(value);
    const label = option ? formatOptionLabel(option) : value;
    const hint = option?.hint?.trim();
    return (
      <span
        key={`${value}-${index}`}
        className="inline-flex items-center gap-1 rounded-full bg-[var(--accent-subtle)] px-2 py-0.5 text-xs text-[var(--accent)]"
      >
        <span className="max-w-[220px] truncate" title={hint ? `${label} · ${hint}` : label}>
          {label}
        </span>
        {multiple && (
          <span className="inline-flex items-center gap-0.5">
            <button
              type="button"
              className="rounded-full px-0.5 leading-none hover:opacity-80 disabled:opacity-30"
              onClick={() => moveValue(index, -1)}
              disabled={index === 0}
              aria-label={`Move ${label} earlier`}
              title="Move earlier"
            >
              <ChevronUp className="size-3" />
            </button>
            <button
              type="button"
              className="rounded-full px-0.5 leading-none hover:opacity-80 disabled:opacity-30"
              onClick={() => moveValue(index, 1)}
              disabled={index === selected.length - 1}
              aria-label={`Move ${label} later`}
              title="Move later"
            >
              <ChevronDown className="size-3" />
            </button>
          </span>
        )}
        <button
          type="button"
          onClick={() => removeValue(index)}
          className="rounded-full px-0.5 leading-none hover:opacity-80"
          aria-label={`Remove ${label}`}
          title="Remove"
        >
          <X className="size-3" />
        </button>
      </span>
    );
  };

  return (
    <div className={cn("flex flex-col gap-2", className)}>
      <div
        className="flex flex-wrap items-center gap-1.5 rounded-[var(--radius-md)] border border-border p-2 min-h-[42px]"
        style={{ background: "var(--bg-elevated)" }}
      >
        {selected.map((value, index) => renderChip(value, index))}
        <label className="flex min-w-[180px] flex-1 items-center gap-2">
          <Search className="size-3.5 shrink-0 text-[var(--muted-foreground)]" />
          <input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                event.preventDefault();
                commitQuery();
              }
            }}
            placeholder={selected.length === 0 ? placeholder : undefined}
            className="w-full bg-transparent text-xs outline-none"
            style={{ color: "var(--text)" }}
          />
        </label>
        {query.trim() ? (
          <button
            type="button"
            onClick={commitQuery}
            className="rounded-full border border-border px-2 py-1 text-[11px] hover:bg-[var(--bg-hover)] transition-colors"
            style={{ color: "var(--text-muted)" }}
          >
            {multiple ? "Add" : selected.length > 0 ? "Replace" : "Set"}
          </button>
        ) : null}
      </div>

      <div className="flex flex-wrap gap-1.5">
        {filteredOptions.map((option) => {
          const label = formatOptionLabel(option);
          const hint = option.hint?.trim();
          const group = option.group?.trim();
          return (
            <button
              key={option.value}
              type="button"
              onClick={() => addValue(option.value)}
              className="rounded-full border border-border px-2 py-1 text-left text-[11px] hover:bg-[var(--bg-hover)] transition-colors"
              style={{ color: "var(--text-muted)" }}
            >
              <span className="flex items-center gap-1.5">
                <span className="font-medium" style={{ color: "var(--text)" }}>
                  {label}
                </span>
                {group ? <span className="text-[10px] uppercase tracking-[0.12em]">{group}</span> : null}
              </span>
              {hint ? <div className="text-[10px] opacity-80">{hint}</div> : null}
            </button>
          );
        })}
        {query.trim() && allowCustom && !filteredOptions.some((option) => option.value === query.trim()) ? (
          <button
            type="button"
            onClick={commitQuery}
            className="rounded-full border border-dashed border-border px-2 py-1 text-left text-[11px] hover:bg-[var(--bg-hover)] transition-colors"
            style={{ color: "var(--text-muted)" }}
          >
            Use custom value: <span style={{ color: "var(--text)" }}>{query.trim()}</span>
          </button>
        ) : null}
        {!query.trim() && filteredOptions.length === 0 ? (
          <div className="text-[11px]" style={{ color: "var(--muted-foreground)" }}>
            No matching suggestions. Type a custom value and press Enter.
          </div>
        ) : null}
      </div>
    </div>
  );
}
